<template>
    <div class="columns">
        <div class="column">
            <div class="is-flex is-justify-content-center mb-5">
                <figure class="image stripe-logo">
                    <img src="https://static.martialmatch.com/images/help-center/90e80a11.png" />
                </figure>
            </div>

            <div>
                <div class="field">
                    <label class="label">{{ translate("pricing.config_name") }}:</label>
                    <div class="control">
                        <input type="text" class="input" v-model="configName" />
                    </div>
                    <p class="help">
                        {{ translate("pricing.config_name_description") }}
                    </p>
                </div>

                <div class="field">
                    <label class="label">{{ translate("pricing.stripe_secret") }}</label>
                    <div class="control">
                        <input type="text" class="input" v-model="secretKey" />
                    </div>
                </div>

                <div class="field is-grouped">
                    <div class="control">
                        <LoadingButton
                            @click="createConfiguration"
                            class="button is-primary"
                            :disabled="!newFormIsValid"
                            :is-loading="creatingNewInProgress"
                        >
                            <span class="icon">
                                <i class="fas fa-check"></i>
                            </span>
                            <span>{{ translate("common.save") }}</span>
                        </LoadingButton>
                    </div>

                    <div class="control">
                        <LoadingButton
                            class="button"
                            @click="checkConnection"
                            :is-loading="connectionCheckInProgress"
                            :disabled="!connectionCanBeChecked"
                        >
                            <span class="icon">
                                <i class="fas fa-plug"></i>
                            </span>
                            <span>{{ translate("pricing.check_connection") }}</span>
                        </LoadingButton>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { ref, computed } from "vue";
    import LoadingButton from "../../Common/LoadingButton.vue";
    import { translate } from "../../Common/translations";
    import { handleError } from "../../Common/errorHandler";
    import { PROVIDER_STRIPE, ProviderEnum, StripeProviderConfiguration } from "../../Api/paymentGatewayConfig";
    import paymentGatewayConfigApi from "../../Api/paymentGatewayConfig";
    import { errorToast, successToast } from "../../Common/toasts";
    import { usePaymentGatewayConfigStore, usePricingSetupStore } from "../pricingSetupApp";

    const store = usePricingSetupStore();
    const paymentGatewayConfigStore = usePaymentGatewayConfigStore();

    const configName = ref("");
    const secretKey = ref("");
    const connectionCheck = ref(false);
    const connectionCheckInProgress = ref(false);
    const creatingNewInProgress = ref(false);

    const userId = computed(() => store.userId);
    const newFormIsValid = computed(() => configName.value.length > 0 && secretKey.value.length > 0 && connectionCheck.value);
    const connectionCanBeChecked = computed(() => secretKey.value.length > 0);

    const createConfiguration = () => {
        creatingNewInProgress.value = true;
        return paymentGatewayConfigApi
            .createConfig<StripeProviderConfiguration>(userId.value, configName.value, ProviderEnum.Stripe, {
                secretKey: secretKey.value,
            })
            .then(createdConfig => {
                paymentGatewayConfigStore.chosenConfig = createdConfig;
                paymentGatewayConfigStore.view = "general_configuration";
            })
            .catch(handleError)
            .finally(() => {
                creatingNewInProgress.value = false;
            });
    };

    const checkConnection = () => {
        connectionCheckInProgress.value = true;
        return paymentGatewayConfigApi
            .checkConnection<StripeProviderConfiguration>(userId.value, {
                provider: PROVIDER_STRIPE,
                providerConfiguration: {
                    secretKey: secretKey.value,
                },
            })
            .then(res => {
                if (res.success) {
                    connectionCheck.value = true;
                    successToast(translate("pricing.check_connection.success"));
                } else {
                    connectionCheck.value = false;
                    errorToast(translate("pricing.check_connection.fail"));
                }
            })
            .catch(handleError)
            .finally(() => {
                connectionCheckInProgress.value = false;
            });
    };
</script>

<style scoped>
    .image.stripe-logo {
        background-color: whitesmoke;
    }
</style>
